import { useErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { type CTWRequestContext, type CTWState, useCTW } from '@ctw/shared/context/ctw-context';
import { useTrackPendingLoad } from '@ctw/shared/context/load-tracking-context';
import type { WithTimerMetricOperationFnContext } from '@ctw/shared/context/telemetry';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useWritebacks, type WritebackState } from '@ctw/shared/context/writeback-provider';
import type { QueryKey } from '@tanstack/query-core';
import { type QueryMeta, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';

export type CustomQueryFnContext = Record<string, unknown>;

export type UseCtwQueryFnContext<CustomContext extends CustomQueryFnContext> = {
  [key in keyof CustomContext &
    keyof CTWRequestContext &
    keyof WithTimerMetricOperationFnContext]: CustomContext[key];
} & {
  requestContext: CTWState['requestContext'];
  graphqlClient: CTWState['graphqlClient'];
  fetchFromFqs: CTWState['fetchFromFqs'];
  ctwFetch: CTWState['ctwFetch'];
  writebacks: WritebackState;
  telemetry: ReturnType<typeof useTelemetry>;
} & WithTimerMetricOperationFnContext;

export interface UseCtwQueryOptions<TData, TContext extends CustomQueryFnContext>
  extends Omit<UseQueryOptions<TData>, 'queryFn' | 'queryKey'> {
  cacheAcrossBuilders?: boolean;
  cacheAcrossUsers?: boolean;
  queryId: string;
  queryKey?: QueryKey;
  queryFn: (context: UseCtwQueryFnContext<TContext>) => Promise<TData>;
  queryFnContext?: TContext;
  queryMeta?: QueryMeta;
}

const DEFAULT_STALE_TIME = 30_000;

export const useCtwQuery = <TData, TContext extends CustomQueryFnContext = CustomQueryFnContext>(
  options: UseCtwQueryOptions<TData, TContext>,
) => {
  const { requestContext, graphqlClient, fetchFromFqs, ctwFetch, ctwBehavioralFlags } = useCTW();
  const writebacks = useWritebacks();
  const telemetry = useTelemetry();
  const errorBoundary = useErrorBoundary();

  // Store closure-only values in refs so they don't invalidate the query options memo.
  // These are injected into the queryFn context but don't affect query identity (queryKey),
  // so changes to them should not trigger memo recalculation or risk duplicate queries.
  const telemetryRef = useRef(telemetry);
  telemetryRef.current = telemetry;
  const writebacksRef = useRef(writebacks);
  writebacksRef.current = writebacks;
  const graphqlClientRef = useRef(graphqlClient);
  graphqlClientRef.current = graphqlClient;
  const fetchFromFqsRef = useRef(fetchFromFqs);
  fetchFromFqsRef.current = fetchFromFqs;
  const ctwFetchRef = useRef(ctwFetch);
  ctwFetchRef.current = ctwFetch;

  const ctwQueryMeta = useMemo(
    () => ({
      ...(errorBoundary ?? {}),
      ...(options.queryMeta ?? {}),
      ctwBehavioralFlags: ctwBehavioralFlags,
      requestContext: {
        ...requestContext,
        authToken: '[REDACTED]',
        headers: telemetryRef.current.redactSensitiveHttpHeaders(requestContext.headers ?? {}),
      },
    }),
    [errorBoundary, ctwBehavioralFlags, options.queryMeta, requestContext],
  );

  const useQueryOptionsWithContextualQueryKeysAndTimedQueryFn: UseQueryOptions<TData> = useMemo(
    () => ({
      staleTime: DEFAULT_STALE_TIME,
      ...options,
      meta: ctwQueryMeta,
      /*
       * Use the consumer-provided query key, but also add in components of the request context
       * to ensure that queries never cache across builders or users (unless specifically requested).
       */
      queryKey: [
        options.queryId,
        ...(options.queryKey ?? []),
        options.cacheAcrossBuilders ? undefined : requestContext.builderId,
        options.cacheAcrossUsers ? undefined : requestContext.authTokenState.zusUserId,
      ].filter((key) => key !== null && key !== undefined),
      /* Wrap `queryFn` in a timer using `Telemetry.withTimerMetric`; only pass `options.queryKey`
       * to `withTimerMetric` so that that's what the query identifies itself as, even though its
       * _real_ query key will include components of the request context.
       */
      queryFn: (queryContext) => {
        if (requestContext.isAuthTokenExpired()) {
          const errorMessage = 'Skipping query because auth token is expired';
          const authTokenExpiredError = new Error(errorMessage);
          telemetryRef.current.logger.warn(errorMessage, {
            query: {
              queryId: options.queryId,
              queryKey: queryContext.queryKey,
            },
          });
          throw authTokenExpiredError;
        }

        const queryFnWithTimerMetric = telemetryRef.current.withTimerMetric(
          'query',
          options.queryId,
          (context) => {
            context.setScopedContextProperty('ctwQueryMeta', ctwQueryMeta);
            return options.queryFn({
              ...(options.queryFnContext ?? {}),
              ...context,
              writebacks: writebacksRef.current,
              requestContext,
              graphqlClient: graphqlClientRef.current,
              fetchFromFqs: fetchFromFqsRef.current,
              ctwFetch: ctwFetchRef.current,
              telemetry: telemetryRef.current,
            });
          },
        );

        return queryFnWithTimerMetric(queryContext);
      },
    }),
    [options, ctwQueryMeta, requestContext],
  );

  const result = useQuery(useQueryOptionsWithContextualQueryKeysAndTimedQueryFn);

  // Contributes to the load time of an enclosing drawer or modal, and does nothing outside one.
  // `isLoading` rather than `isFetching`, so that revalidating already-rendered data doesn't hold
  // an overlay in "loading" — the user is looking at content either way.
  useTrackPendingLoad(result.isLoading, result.isError);

  return result;
};
